import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest, merge, Observable, Subject, Subscription } from 'rxjs';
import { find, omit } from 'lodash';
import { DsButtonVariants, DsModalService, DsToggleLabelPosition } from '@levelaccess/design-system';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';

import { map, skip, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { DropdownCellComponent } from '@client/app/components/table/ngb-table/cells/dropdown-cell/dropdown-cell.component';
import { BusMessageChannels, BusMessageService } from '../../../../../../../services/bus-message.service';
import { ManualAuditIssueService } from '../../../../../../../services/manual-audit-issue.service';
import { CustomValidators } from '../../../../../../../services/helpers/form-custom-validators';
import { $auditIssueFields } from '../../../../../../../../../shared/constants/manual-audit-issues';
import { IAuditIssueUpdateFormData } from '../../../../../../../../../shared/interfaces/audit-issue.interface';
import {
  $auditFinding,
  $auditFindingViewport,
  AuditFindingIssueEffort,
  AuditFindingIssueFrequency,
  DRAFT_FINDING_TITLE_PREFIX,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENTS_SIZE,
  VIEWPORT_HEIGHT_MAX_VALUE,
  VIEWPORT_HEIGHT_MIN_VALUE,
  VIEWPORT_WIDTH_MAX_VALUE,
  VIEWPORT_WIDTH_MIN_VALUE,
} from '../../../../../../../../../shared/constants/audit-finding';
import { IPopulatedManualAuditIssueServerResponse } from '../../../../../../../../../shared/interfaces/manual-audit.interface';
import { $uploads } from '../../../../../../../../../shared/constants/uploads';
import { LoadErrorHandler } from '../../../../../../../services/load-error-handler';
import { ErrorMessageService } from '../../../../../../../services/error-message.service';
import { ICurrentSelectedProperty, UserService } from '../../../../../../../services/user.service';
import { UserPropertyService } from '../../../../../../../services/user-property.service';
import { FeatureFlagService } from '../../../../../../../services/feature-flag/feature-flag.service';
import { FormService } from '../../../../../../../services/form.service';
import { IAuditFinding } from '../../../../../../../../../shared/interfaces/audit-finding.interface';
import { NotificationPosition } from '../../../../../../../models/notification.model';
import { NotificationService } from '../../../../../../../services/notification.service';
import { $auditRule } from '../../../../../../../../../shared/constants/audit-rule';
import { IUploadClient } from '../../../../../../../../../shared/interfaces/uploads.interface';
import {
  MimeTypes,
  SupportedManualAuditAttachmentExtensions,
  SupportedManualAuditAttachmentMimeType,
} from '../../../../../../../../../shared/constants/mime-type';
import { TranslateService } from '../../../../../../../translate/translate.service';
import { CommonUtility } from '../../../../../../../utility/common.utility';
import {
  IManualAuditFindingRuleWithPopulatedSuccessCriteria,
  IPopulatedManualFindingClient,
} from '../../../../../../../../../shared/interfaces/manual-audit-finding.interface';
import { $severity } from '../../../../../../../../../shared/constants/accessibility';
import { SharedCollectionsUtility } from '../../../../../../../../../shared/utils/collections.utility';
import { IFileDragAndDropContext } from '../../../../../../../components/form-field/file-upload/file-drag-and-drop/file-drag-and-drop.component';
import { AngularUtility } from '../../../../../../../utility/angular.utility';
import { FeatureFlagCollection } from '../../../../../../../../../shared/interfaces/feature-flag.interface';
import { TenantSeveritiesService } from '../../../../../../../services/tenant-severities.service';
import { ICustomSeveritiesMap, ICustomSeverity } from '../../../../../../../../../shared/interfaces/tenant.interface';
import { $customSeverity } from '../../../../../../../../../shared/constants/tenant';
import { SharedTextUtility } from '../../../../../../../../../shared/utils/text.utility';
import { IAuditRuleServerItem } from '../../../../../../../../../shared/interfaces/audit-rule.interface';
import { $scanScope } from '../../../../../../../../../shared/constants/scan';
import { IScanScope } from '../../../../../../../../../shared/interfaces/scan.interface';
import { IMatchComponent } from '../../../../../../../../../shared/interfaces/match-component.interface';
import { $matchComponent } from '../../../../../../../../../shared/constants/match-component';
import { IManualFindingEdit } from '../../../../../../../interfaces/manual-evaluation.interface';

@Component({
  selector: 'app-edit-manual-audit-issue',
  templateUrl: './edit-manual-audit-issue.component.html',
  styleUrls: ['./edit-manual-audit-issue.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditManualAuditIssueComponent implements OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private save$: Subject<void> = new Subject<void>();
  private removeAttachment$: Subject<string> = new Subject<string>();
  private selectedRule$: Subject<IAuditRuleServerItem> = new Subject<IAuditRuleServerItem>();
  private selectedRuleObjectId$: Observable<string>;
  private static INPUT_FIELD_MAX_LENGTH: number = 10000;

  @Input() public evaluationScanId: string;
  @Input() public manualAuditIssueId: string;
  @Input() public openedFromActionsDropDown: boolean;

  public onSave: EventEmitter<void> = new EventEmitter<void>();
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly DsToggleLabelPosition: typeof DsToggleLabelPosition = DsToggleLabelPosition;
  public readonly DRAFT_FINDING_TITLE_PREFIX: string = DRAFT_FINDING_TITLE_PREFIX;
  public formValidationRequest$: Subject<void>;
  public form: UntypedFormGroup;
  public $auditIssueFields: typeof $auditIssueFields = $auditIssueFields;
  public $auditFinding: typeof $auditFinding = $auditFinding;
  public $auditFindingViewport: typeof $auditFindingViewport = $auditFindingViewport;
  public auditFindingIssueFrequencyValues: { value: string; name: string }[];
  public auditFindingIssueEffortValues: { value: string; name: string }[];
  public auditFindingIssueSeverityValues$: Observable<{ value: string; name: string; isCustom: boolean }[]>;
  public customSeveritiesData: ICustomSeveritiesMap;
  public customSeverityKeysSortedByRank: $severity[];
  public serverResponse: IManualFindingEdit;
  public serverResponse$: Observable<IPopulatedManualAuditIssueServerResponse>;
  public mimeType: typeof MimeTypes;
  public isDragAreaActive: boolean;
  public selectedAttachments: File[];
  public fileUploadContext: IFileDragAndDropContext;
  public FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  public $matchComponent: typeof $matchComponent = $matchComponent;
  public components$: Observable<IMatchComponent[]>;
  public hasManualEvaluationScope$: Observable<boolean>;
  public errors: Record<string, Observable<string | null>>;
  public testGuideId$: Observable<string>;
  public viewportWidthMinValue: number = VIEWPORT_WIDTH_MIN_VALUE;
  public viewportWidthMaxValue: number = VIEWPORT_WIDTH_MAX_VALUE;
  public viewportHeightMinValue: number = VIEWPORT_HEIGHT_MIN_VALUE;
  public viewportHeightMaxValue: number = VIEWPORT_HEIGHT_MAX_VALUE;
  protected findingRuleFromResponse: IManualAuditFindingRuleWithPopulatedSuccessCriteria;
  public isDraft: Signal<boolean>;
  public canToggleDraft$: Observable<boolean>;
  public canToggleDraft: Signal<boolean>;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private manualAuditIssueService: ManualAuditIssueService,
    private loadErrorHandler: LoadErrorHandler,
    private element: ElementRef<Element>,
    private errorMessageService: ErrorMessageService,
    private userPropertyService: UserPropertyService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private tenantSeveritiesService: TenantSeveritiesService,
    private formService: FormService,
    private busMessageService: BusMessageService,
    private userService: UserService,
    private featureFlagService: FeatureFlagService,
    public modal: NgbActiveOffcanvas,
    public modalService: DsModalService,
  ) {
    this.formValidationRequest$ = new Subject<void>();
    this.auditFindingIssueFrequencyValues = Object.keys(AuditFindingIssueFrequency).map((key: string) => ({
      value: key,
      name: `issue_field_frequency_${key}`,
    }));
    this.auditFindingIssueEffortValues = Object.keys(AuditFindingIssueEffort).map((key: string) => ({
      value: key,
      name: `label_issue_effort_${key}`,
    }));

    this.mimeType = MimeTypes;
    this.isDragAreaActive = false;
    this.selectedAttachments = [];

    this.setupForm();
    this.fileUploadContext = this.getFileUploadContext();
    this.canToggleDraft$ = combineLatest([
      this.featureFlagService.variation$(FeatureFlagCollection.findingsWorkflowAndEditing, false),
      this.userService.hasTenantStaffAccess(),
    ]).pipe(
      map(([ffEnabled, isStaff]: [boolean, boolean]): boolean => ffEnabled && isStaff),
      AngularUtility.shareRef(),
    );
    this.canToggleDraft = toSignal(this.canToggleDraft$, { initialValue: false });
  }

  private setupForm(): void {
    this.form = this.formBuilder.group({
      [$auditIssueFields.summary]: this.formBuilder.control('', [
        CustomValidators.required,
        CustomValidators.validateIsEmpty,
        Validators.maxLength(EditManualAuditIssueComponent.INPUT_FIELD_MAX_LENGTH),
      ]),
      [$auditIssueFields.screen]: this.formBuilder.control('', [
        CustomValidators.required,
        CustomValidators.validateIsEmpty,
        Validators.maxLength(EditManualAuditIssueComponent.INPUT_FIELD_MAX_LENGTH),
      ]),
      [$auditIssueFields.url]: this.formBuilder.control('', [
        CustomValidators.required,
        CustomValidators.validateIsEmpty,
        CustomValidators.urlValidator,
      ]),
      [$auditIssueFields.issueFrequency]: this.formBuilder.control('', [
        CustomValidators.required,
        CustomValidators.validateIsEmpty,
      ]),
      [$auditIssueFields.effort]: this.formBuilder.control('', [CustomValidators.required, CustomValidators.validateIsEmpty]),
      [$auditIssueFields.severity]: this.formBuilder.control('', [CustomValidators.required, CustomValidators.validateIsEmpty]),
      [$auditIssueFields.instances]: this.formBuilder.control('', [
        Validators.maxLength(EditManualAuditIssueComponent.INPUT_FIELD_MAX_LENGTH),
      ]),
      [$auditIssueFields.screenshot]: this.formBuilder.control('', [
        CustomValidators.domainRestrictionsValidator([/.*\.(essentialaccessibility|screencast)\.com/], 'isDomainUnavailable'),
      ]),
      [$auditIssueFields.actualResult]: this.formBuilder.control('', [
        Validators.maxLength(EditManualAuditIssueComponent.INPUT_FIELD_MAX_LENGTH),
      ]),
      [$auditIssueFields.stepsToReproduce]: this.formBuilder.control('', [
        Validators.maxLength(EditManualAuditIssueComponent.INPUT_FIELD_MAX_LENGTH),
      ]),
      [$auditIssueFields.userImpact]: this.formBuilder.control('', [
        Validators.maxLength(EditManualAuditIssueComponent.INPUT_FIELD_MAX_LENGTH),
      ]),
      [$auditIssueFields.recommendation]: this.formBuilder.control('', [
        Validators.maxLength(EditManualAuditIssueComponent.INPUT_FIELD_MAX_LENGTH),
      ]),
      [$auditIssueFields.ruleId]: this.formBuilder.control('', [
        CustomValidators.required,
        CustomValidators.validateIsEmpty,
        CustomValidators.validateProvidedDisabledRuleNotSelected(() => this.findingRuleFromResponse),
      ]),
      [$auditIssueFields.files]: this.formBuilder.control(''),
      [$auditFindingViewport.width]: this.formBuilder.control(null, [
        CustomValidators.required,
        CustomValidators.validateInteger,
        CustomValidators.min(VIEWPORT_WIDTH_MIN_VALUE),
        CustomValidators.max(VIEWPORT_WIDTH_MAX_VALUE),
      ]),
      [$auditFindingViewport.height]: this.formBuilder.control(null, [
        CustomValidators.required,
        CustomValidators.validateInteger,
        CustomValidators.min(VIEWPORT_HEIGHT_MIN_VALUE),
        CustomValidators.max(VIEWPORT_HEIGHT_MAX_VALUE),
      ]),
      isRelatedToComponent: this.formBuilder.control(false),
      [$auditFinding.componentId]: this.formBuilder.control(''),
      [$auditFinding.isDraft]: this.formBuilder.control(false),
    });

    this.errors = {
      [$auditFinding.componentId]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditFinding.componentId), $auditIssueFields.component)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.summary]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.summary), $auditIssueFields.summary)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.screen]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.screen), $auditIssueFields.screen)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.url]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.url), $auditIssueFields.url)
        .pipe(AngularUtility.shareRef()),
      [$auditFindingViewport.width]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditFindingViewport.width), $auditFindingViewport.width, {
          required: this.translateService.instant('enter_viewport_width'),
          min: this.translateService.instant('enter_viewport_width_between', [
            VIEWPORT_WIDTH_MIN_VALUE,
            VIEWPORT_WIDTH_MAX_VALUE,
          ]),
          max: this.translateService.instant('enter_viewport_width_between', [
            VIEWPORT_WIDTH_MIN_VALUE,
            VIEWPORT_WIDTH_MAX_VALUE,
          ]),
        })
        .pipe(AngularUtility.shareRef()),
      [$auditFindingViewport.height]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditFindingViewport.height), $auditFindingViewport.height, {
          required: this.translateService.instant('enter_viewport_height'),
          min: this.translateService.instant('enter_viewport_height_between', [
            VIEWPORT_HEIGHT_MIN_VALUE,
            VIEWPORT_HEIGHT_MAX_VALUE,
          ]),
          max: this.translateService.instant('enter_viewport_height_between', [
            VIEWPORT_HEIGHT_MIN_VALUE,
            VIEWPORT_HEIGHT_MAX_VALUE,
          ]),
        })
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.issueFrequency]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.issueFrequency), $auditIssueFields.issueFrequency)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.effort]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.effort), $auditIssueFields.effort)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.instances]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.instances), $auditIssueFields.instances)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.screenshot]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.screenshot), $auditIssueFields.screenshot)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.actualResult]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.actualResult), $auditIssueFields.actualResult)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.stepsToReproduce]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.stepsToReproduce), $auditIssueFields.stepsToReproduce)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.userImpact]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.userImpact), $auditIssueFields.userImpact)
        .pipe(AngularUtility.shareRef()),
      [$auditIssueFields.recommendation]: this.formService
        .buildFormControlErrorMessage$(this.form.get($auditIssueFields.recommendation), $auditIssueFields.recommendation)
        .pipe(AngularUtility.shareRef()),
    };

    this.subscriptions.add(
      this.formValidationRequest$.subscribe(() => {
        // Mark all form controls with error messages as dirty and update validity
        this.form.get($auditFinding.componentId).updateValueAndValidity();
        this.form.get($auditFinding.componentId).markAsDirty();

        this.form.get($auditIssueFields.summary).updateValueAndValidity();
        this.form.get($auditIssueFields.summary).markAsDirty();

        this.form.get($auditIssueFields.screen).updateValueAndValidity();
        this.form.get($auditIssueFields.screen).markAsDirty();

        this.form.get($auditIssueFields.url).updateValueAndValidity();
        this.form.get($auditIssueFields.url).markAsDirty();

        this.form.get($auditFindingViewport.width).updateValueAndValidity();
        this.form.get($auditFindingViewport.width).markAsDirty();

        this.form.get($auditFindingViewport.height).updateValueAndValidity();
        this.form.get($auditFindingViewport.height).markAsDirty();

        this.form.get($auditIssueFields.issueFrequency).updateValueAndValidity();
        this.form.get($auditIssueFields.issueFrequency).markAsDirty();

        this.form.get($auditIssueFields.effort).updateValueAndValidity();
        this.form.get($auditIssueFields.effort).markAsDirty();

        this.form.get($auditIssueFields.instances).updateValueAndValidity();
        this.form.get($auditIssueFields.instances).markAsDirty();

        this.form.get($auditIssueFields.screenshot).updateValueAndValidity();
        this.form.get($auditIssueFields.screenshot).markAsDirty();

        this.form.get($auditIssueFields.actualResult).updateValueAndValidity();
        this.form.get($auditIssueFields.actualResult).markAsDirty();

        this.form.get($auditIssueFields.stepsToReproduce).updateValueAndValidity();
        this.form.get($auditIssueFields.stepsToReproduce).markAsDirty();

        this.form.get($auditIssueFields.userImpact).updateValueAndValidity();
        this.form.get($auditIssueFields.userImpact).markAsDirty();

        this.form.get($auditIssueFields.recommendation).updateValueAndValidity();
        this.form.get($auditIssueFields.recommendation).markAsDirty();

        this.form.markAllAsTouched();
        this.changeDetectorRef.detectChanges();
      }),
    );

    this.subscriptions.add(
      this.form.get('isRelatedToComponent').valueChanges.subscribe((isRelated: boolean) => {
        const componentControl = this.form.get($auditFinding.componentId);
        if (isRelated) {
          componentControl.setValidators([CustomValidators.required]);
        } else {
          componentControl.clearValidators();
        }
        componentControl.updateValueAndValidity();
      }),
    );

    this.isDraft = toSignal(this.form.get($auditFinding.isDraft).valueChanges, {
      initialValue: this.form.get($auditFinding.isDraft).value,
    });
  }

  public onDraftToggleChange(isDraft: boolean): void {
    this.form.get($auditFinding.isDraft).setValue(isDraft);
  }

  private onGetIssueError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('view_finding_error', response, EditManualAuditIssueComponent.name);
  }

  private onSaveIssueError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('update_finding_details_error', response, EditManualAuditIssueComponent.name);
  }

  private onRemoveAttachmentSuccess(response: string[]): void {
    this.notificationService.success(
      this.translateService.instant('success_removing_manual_issue_attachment'),
      NotificationPosition.Toast,
    );

    this.serverResponse[$auditFinding.attachment] = (this.serverResponse[$auditFinding.attachment] as IUploadClient[]).filter(
      (attachment: IUploadClient): boolean => response.includes(attachment[$uploads._id] as string) === false,
    );

    this.changeDetectorRef.detectChanges();
  }

  private onRemoveAttachmentError(response: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('remove_manual_audit_issue_attachment_error', response, EditManualAuditIssueComponent.name);
  }

  private onSaveIssueSuccess(): void {
    this.notificationService.success(
      this.translateService.instant('edit_finding_success'),
      NotificationPosition.Toast,
      true,
      undefined,
      undefined,
      undefined,
      () => {
        if (this.openedFromActionsDropDown) {
          DropdownCellComponent.setFocusOnLastOpened();
        }
      },
    );
    this.onSave.emit();
    this.modalService.closeCurrentModal();
  }

  private getIssue(): Observable<IPopulatedManualAuditIssueServerResponse> {
    return this.manualAuditIssueService.getIssue(this.evaluationScanId, this.manualAuditIssueId).pipe(AngularUtility.shareRef());
  }

  private getManualEvaluationScope(): Observable<IScanScope | undefined> {
    return this.manualAuditIssueService.getManualEvaluationScope(this.evaluationScanId).pipe(AngularUtility.shareRef());
  }

  private saveIssue(): Observable<IAuditFinding> {
    return this.save$.pipe(
      withLatestFrom(this.userPropertyService.currentSelectedProperty(), this.selectedRuleObjectId$, this.canToggleDraft$),
      switchMap(
        ([, selectedProperty, selectedRuleObjectId, canToggleDraft]: [void, ICurrentSelectedProperty, string, boolean]) => {
          const isDraftValue: boolean | undefined = canToggleDraft ? Boolean(this.form.value[$auditFinding.isDraft]) : undefined;
          const baseSummary: string = this.form.value[$auditIssueFields.summary] ?? '';

          const requestData: IAuditIssueUpdateFormData = {
            ...(omit(this.form.value, [$auditIssueFields.effort, 'isRelatedToComponent']) as IAuditIssueUpdateFormData),
            [$auditFinding.ruleId]: selectedRuleObjectId,
            [$auditFinding.functionalImpact]: this.form.value.effort,
            [$auditFinding.isDraft]: isDraftValue,
            [$auditIssueFields.summary]: isDraftValue ? DRAFT_FINDING_TITLE_PREFIX + baseSummary : baseSummary,
          };

          if (!this.form.value.isRelatedToComponent) {
            delete requestData[$auditFinding.componentId];
          }

          if (isDraftValue === undefined) {
            delete requestData[$auditFinding.isDraft];
          }

          delete requestData[$auditIssueFields.files];

          const formData: FormData = CommonUtility.createFormData(requestData);
          let counterFiles: number = 0;

          const addFileToFormData = (file: File): void => {
            formData.append($auditFinding.attachment + `_${counterFiles}`, file, file.name);
            counterFiles += 1;
          };

          this.selectedAttachments.forEach(addFileToFormData);

          return this.manualAuditIssueService.updateQAIssue(
            selectedProperty.workspaceId,
            this.evaluationScanId,
            selectedProperty.digitalPropertyId,
            this.manualAuditIssueId,
            formData,
          );
        },
      ),
    );
  }

  private closeOnPropertyChange(): void {
    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.digitalPropertyChange)
        .pipe(skip(1))
        .subscribe(() => this.modalService.closeCurrentModal()),
    );
  }

  private buildCustomSeverityObservables(): void {
    this.auditFindingIssueSeverityValues$ = this.tenantSeveritiesService.getAll().pipe(
      map((severities: ICustomSeveritiesMap) => {
        this.customSeveritiesData = severities;
        this.customSeverityKeysSortedByRank = this.tenantSeveritiesService.getCustomSeverityKeysSortedByRank(severities);
        const getDisplayValues = ([key, severity]: [string, ICustomSeverity]): {
          name: string;
          isCustom: boolean;
          value: string;
        } => {
          return {
            name: SharedTextUtility.capitalize(severity[$customSeverity.label]),
            value: key,
            isCustom: !severity[$customSeverity.system],
          };
        };

        const severityRecord: [string, ICustomSeverity][] = Array.from(severities.entries());

        return severityRecord
          .sort(
            ([_, severity1]: [string, ICustomSeverity], [__, severity2]: [string, ICustomSeverity]) =>
              severity2[$customSeverity.rank] - severity1[$customSeverity.rank],
          )
          .map(getDisplayValues);
      }),
      AngularUtility.shareRef(),
    );
  }

  public onUpdateSelectedRule(rule: IAuditRuleServerItem): void {
    this.selectedRule$.next(rule);
  }

  public onExistingAttachmentsChange(updated: IUploadClient[]): void {
    const keySelector = (file: IUploadClient): string => file._id;
    const existing: IUploadClient[] = this.serverResponse.attachment ?? [];
    const removedAttachments: IUploadClient[] = SharedCollectionsUtility.difference(updated, existing, keySelector);

    for (const removedAttachment of removedAttachments) {
      this.removeAttachment$.next(removedAttachment[$uploads._id]);
    }
  }

  public uploadFiles(files: File[]): void {
    this.selectedAttachments = files;
  }

  // Modal is dynamically loaded. So lifecycle management has to be done manually.
  public modalInit(): void {
    this.serverResponse$ = this.getIssue();
    this.subscriptions.add(this.serverResponse$.subscribe({ error: this.onGetIssueError.bind(this) }));

    const scope$: Observable<IScanScope | undefined> = this.getManualEvaluationScope();

    this.testGuideId$ = scope$.pipe(map((scope: IScanScope | undefined) => scope?.testGuideId));

    this.components$ = combineLatest([scope$, this.serverResponse$]).pipe(
      map(([scope, { issue }]: [IScanScope | undefined, IPopulatedManualAuditIssueServerResponse]) => {
        return find(scope[$scanScope.pages], { pageId: issue.documentId.pageId })?.components ?? [];
      }),
    );

    this.hasManualEvaluationScope$ = scope$.pipe(
      map((scope: IScanScope | undefined): boolean => {
        return scope !== undefined && Array.isArray(scope[$scanScope.pages]) && scope[$scanScope.pages].length > 0;
      }),
    );

    this.buildCustomSeverityObservables();

    this.subscriptions.add(
      combineLatest([this.serverResponse$, this.auditFindingIssueSeverityValues$])
        .pipe(
          withLatestFrom(this.canToggleDraft$),
          map(
            ([[response], canToggleDraft]: [
              [IPopulatedManualAuditIssueServerResponse, unknown],
              boolean,
            ]): IManualFindingEdit => {
              const issue: IPopulatedManualFindingClient = response.issue;
              const summary: string = issue[$auditIssueFields.summary] ?? '';
              const shouldStripDraftPrefix: boolean =
                canToggleDraft && Boolean(issue[$auditFinding.isDraft]) && summary.startsWith(DRAFT_FINDING_TITLE_PREFIX);
              return {
                ...issue,
                [$auditIssueFields.summary]: shouldStripDraftPrefix ? summary.slice(DRAFT_FINDING_TITLE_PREFIX.length) : summary,
                [$auditIssueFields.url]: response.issueUrl,
                [$auditIssueFields.screenshot]: (Array.isArray(issue.screenshot) ? issue.screenshot : []).join('\n'),
                [$auditIssueFields.effort]: issue.functionalImpact,
                [$auditIssueFields.ruleId]: issue.ruleId[$auditRule.ruleId],
                [$auditFindingViewport.width]: issue.viewport?.[$auditFindingViewport.width],
                [$auditFindingViewport.height]: issue.viewport?.[$auditFindingViewport.height],
                isRelatedToComponent: Boolean(issue[$auditFinding.component]),
                [$auditIssueFields.component]: issue[$auditFinding.component]?._id ?? null,
              };
            },
          ),
        )
        .subscribe((issue: IManualFindingEdit) => {
          this.serverResponse = issue;
          this.form.patchValue(issue);
          this.changeDetectorRef.markForCheck();
        }),
    );

    this.subscriptions.add(
      this.serverResponse$
        .pipe(map((response: IPopulatedManualAuditIssueServerResponse) => response.issue.ruleId))
        .subscribe((rule: IManualAuditFindingRuleWithPopulatedSuccessCriteria) => {
          this.findingRuleFromResponse = rule;
        }),
    );

    // selectedRuleObjectId can come either from when we loaded the rule for the first time, or from the user when
    // they select a different rule in the edit box
    this.selectedRuleObjectId$ = merge(
      this.selectedRule$.pipe(map((rule: IAuditRuleServerItem) => rule[$auditRule._id])),
      this.serverResponse$.pipe(
        map((response: IPopulatedManualAuditIssueServerResponse): string => response.issue[$auditFinding.ruleId][$auditRule._id]),
      ),
    );

    this.subscriptions.add(
      this.saveIssue()
        .pipe(tap((): void => this.onSaveIssueSuccess()))
        .subscribe({
          error: this.onSaveIssueError.bind(this),
        }),
    );

    const currentSelectedProperty$: Observable<ICurrentSelectedProperty> = this.userPropertyService.currentSelectedProperty();
    this.subscriptions.add(
      this.removeAttachment$
        .pipe(
          withLatestFrom(currentSelectedProperty$),
          switchMap(([attachmentId, selectedProperty]: [string, ICurrentSelectedProperty]) =>
            this.manualAuditIssueService.deleteAttachment(
              selectedProperty.workspaceId,
              selectedProperty.digitalPropertyId,
              this.evaluationScanId,
              this.manualAuditIssueId,
              attachmentId,
            ),
          ),
        )
        .subscribe({
          next: this.onRemoveAttachmentSuccess.bind(this),
          error: this.onRemoveAttachmentError.bind(this),
        }),
    );

    this.subscriptions.add(
      this.selectedRule$.subscribe((rule: IAuditRuleServerItem) => {
        this.form.patchValue({
          [$auditIssueFields.summary]: rule[$auditRule.description],
          [$auditIssueFields.effort]: rule[$auditRule.functionalImpact] ?? AuditFindingIssueEffort.low,
          [$auditIssueFields.severity]: rule[$auditRule.severity],
          [$auditIssueFields.actualResult]: rule[$auditRule.actualResult],
          [$auditIssueFields.stepsToReproduce]: rule[$auditRule.stepsToReproduce],
          [$auditIssueFields.userImpact]: rule[$auditRule.userImpact],
          [$auditIssueFields.recommendation]: rule[$auditRule.recommendation],
        });
      }),
    );

    this.closeOnPropertyChange();
  }

  public submitForm(): void {
    this.form.get($auditIssueFields.files).updateValueAndValidity();
    this.formValidationRequest$.next();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    this.save$.next();
  }

  private getFileUploadContext(): IFileDragAndDropContext {
    return {
      label: this.translateService.instant('label_upload_attachments'),
      tableTitle: this.translateService.instant('attachments'),
      field: $auditIssueFields.files,
      acceptedFileExtensions: SupportedManualAuditAttachmentExtensions,
      acceptedMimeTypes: SupportedManualAuditAttachmentMimeType,
      maxFileSize: MAX_ATTACHMENTS_SIZE,
      maxTotalFileSize: MAX_ATTACHMENTS_SIZE,
      maxTotalFileQuantity: MAX_ATTACHMENTS,
      required: false,
    };
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
